'use client'

import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { playV2FocusRing } from '@/lib/play/playV2Focus'
import { SURFACE_FG_ATTR } from '@/lib/design/surfaceForeground'
import {
  wagamourV2ModalBackdrop,
  wagamourV2ModalMaxHeight,
  wagamourV2ModalZIndex,
} from '@/lib/design/v2Modal'
import { wagamourV2Surface, wagamourV2SurfaceBlur, wagamourV2ModalShellBodyGlow, wagamourV2ModalShellOuterShadow } from '@/lib/design/v2Surface'
import { WagamourV2Surface } from '@/components/v2/WagamourV2Surface'
import {
  v2ModalShellBorderGradient,
  v2ModalShellDefaultTextStyle,
  v2ModalShellInnerRadiusClassName,
  v2ModalShellInnerRadiusStyle,
  v2ModalShellRadiusClassName,
  v2ModalShellRadiusStyle,
} from '@/lib/design/v2ModalContent'
import { isNativeCapacitorApp } from '@/lib/pwa/isNativeCapacitorApp'
import {
  lockDocumentScroll,
  unlockDocumentScroll,
} from '@/lib/dom/documentScrollLock'

export interface WagamourV2ModalProps {
  isOpen: boolean
  onClose: () => void
  /** Header slot — title row, icon, etc. */
  header?: ReactNode
  /** Scrollable body. */
  children: ReactNode
  /** Footer slot — CTA row. */
  footer?: ReactNode
  className?: string
  contentClassName?: string
  showCloseButton?: boolean
  preventClose?: boolean
  /** Center dialog (default) or bottom sheet presentation. */
  presentation?: 'center' | 'sheet'
  /** When set, portal inside this element (e.g. bounded preview canvas) instead of document.body. */
  portalContainer?: HTMLElement | null
  /** Accessible name for the dialog (prefer pairing with a titled h2 id). */
  ariaLabelledBy?: string
  /** Fallback accessible name when no labelled-by id is available. */
  ariaLabel?: string
}

/**
 * V2 modal shell — glass container over blurred backdrop.
 * Used by UpgradeModal and V2-skinned ModalDialog / BottomSheet surfaces.
 *
 * SHELL LAYERING (locked — see docs/V2_MODAL_DESIGN_STANDARD.md):
 *   1. outer wrapper  → drop shadow only, no overflow clip
 *   2. ring host      → p-px gradient perimeter, outer radius (28px / 32px top)
 *   3. clip host      → panelRef, overflow-hidden, inner radius, glass + blur
 *   4. fillShell      → WagamourV2Surface content/bloom only
 *
 * Do NOT merge border, backdrop-filter, radius, and overflow-hidden onto one element.
 */
export function WagamourV2Modal({
  isOpen,
  onClose,
  header,
  children,
  footer,
  className,
  contentClassName,
  showCloseButton = true,
  preventClose = false,
  presentation = 'center',
  portalContainer = null,
  ariaLabelledBy,
  ariaLabel,
}: WagamourV2ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null)
  const previousActiveElement = useRef<HTMLElement | null>(null)
  const [portalEl, setPortalEl] = useState<HTMLElement | null>(null)
  const anchoredToContainer = Boolean(portalContainer)

  useEffect(() => {
    setPortalEl(portalContainer ?? document.body)
  }, [portalContainer])

  useEffect(() => {
    if (!isOpen) return

    previousActiveElement.current = document.activeElement as HTMLElement

    if (!anchoredToContainer) {
      // Shared lock — captures scrollY before position:fixed; restores on unlock.
      // Critical for app-beta-document-scroll + repeat open/dismiss on PWA.
      lockDocumentScroll()
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
    } else if (portalContainer) {
      portalContainer.style.overflow = 'hidden'
    }

    const focusableElements = panelRef.current?.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    )
    const firstElement = focusableElements?.[0] as HTMLElement
    firstElement?.focus({ preventScroll: true })

    return () => {
      if (!anchoredToContainer) {
        document.body.style.paddingRight = ''
        unlockDocumentScroll()
      } else if (portalContainer) {
        portalContainer.style.overflow = ''
      }
      // Do not refocus the prior control — iOS PWA often ignores preventScroll and
      // jumps the document when returning focus after dismiss.
      previousActiveElement.current = null
    }
  }, [isOpen, anchoredToContainer, portalContainer])

  useEffect(() => {
    if (!isOpen || preventClose) return

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose, preventClose])

  /**
   * Capacitor Android/iOS hardware/system Back: close the open modal/sheet
   * instead of exiting the app or navigating the shell away.
   * Listener is registered only while open (restores default back when closed).
   */
  useEffect(() => {
    if (!isOpen || preventClose) return
    if (typeof window === 'undefined' || !isNativeCapacitorApp()) return

    let remove: (() => void) | undefined
    let cancelled = false

    void (async () => {
      try {
        const { App } = await import('@capacitor/app')
        if (cancelled) return
        const handle = await App.addListener('backButton', () => {
          onClose()
        })
        remove = () => {
          void handle.remove()
        }
      } catch {
        /* App plugin unavailable — Escape/backdrop still close */
      }
    })()

    return () => {
      cancelled = true
      remove?.()
    }
  }, [isOpen, onClose, preventClose])

  useEffect(() => {
    if (!isOpen) return

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return

      const focusableElements = panelRef.current?.querySelectorAll(
        'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
      )
      if (!focusableElements || focusableElements.length === 0) return

      const firstElement = focusableElements[0] as HTMLElement
      const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else if (document.activeElement === lastElement) {
        e.preventDefault()
        firstElement.focus()
      }
    }

    document.addEventListener('keydown', handleTab)
    return () => document.removeEventListener('keydown', handleTab)
  }, [isOpen])

  if (!isOpen || !portalEl) return null

  const isSheet = presentation === 'sheet'

  return createPortal(
    <div
      className={cn(
        anchoredToContainer ? 'absolute inset-0' : 'fixed inset-0',
        isSheet ? 'flex flex-col justify-end' : 'overflow-y-auto overscroll-y-contain'
      )}
      style={{ zIndex: wagamourV2ModalZIndex.backdrop }}
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget && !preventClose) onClose()
      }}
    >
      {/* Backdrop */}
      <div
        className={cn(anchoredToContainer ? 'absolute inset-0' : 'fixed inset-0', 'pointer-events-none')}
        aria-hidden
        style={{
          zIndex: wagamourV2ModalZIndex.backdrop,
          background: wagamourV2ModalBackdrop.background,
          backdropFilter: `blur(${wagamourV2ModalBackdrop.backdropBlur})`,
          WebkitBackdropFilter: `blur(${wagamourV2ModalBackdrop.backdropBlur})`,
        }}
      />

      {/* Panel positioning layer */}
      <div
        className={cn(
          'relative flex w-full',
          isSheet
            ? 'items-end px-0 pb-0'
            : 'min-h-[100dvh] items-center justify-center px-3 sm:px-4 box-border',
          !isSheet && 'py-[max(12px,env(safe-area-inset-top,0px))] pb-[max(12px,env(safe-area-inset-bottom,0px))]'
        )}
        style={{ zIndex: wagamourV2ModalZIndex.panel }}
        onClick={(e) => {
          if (!isSheet && e.target === e.currentTarget && !preventClose) onClose()
        }}
      >
        {/* Shell layering locked — docs/V2_MODAL_DESIGN_STANDARD.md. Do not collapse border/blur/radius/overflow. */}
        <div
          className={cn(
            'relative mx-auto w-full max-w-md',
            isSheet && 'max-w-none',
            className
          )}
          style={{ touchAction: 'manipulation', boxShadow: wagamourV2ModalShellOuterShadow }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Ring host: gradient perimeter + outer radius (28px center / 32px sheet top) */}
          <div
            className={cn('p-px', v2ModalShellRadiusClassName(isSheet ? 'sheet' : 'center'))}
            style={{
              background: v2ModalShellBorderGradient,
              ...v2ModalShellRadiusStyle(isSheet ? 'sheet' : 'center'),
            }}
          >
            {/* Clip host (panelRef): overflow-hidden + glass + blur — no CSS border here */}
            <div
              ref={panelRef}
              role="dialog"
              aria-modal="true"
              aria-labelledby={ariaLabelledBy}
              aria-label={ariaLabelledBy ? undefined : ariaLabel}
              data-surface-fg={SURFACE_FG_ATTR.darkGlass}
              className={cn(
                'relative flex w-full flex-col overflow-hidden',
                v2ModalShellInnerRadiusClassName(isSheet ? 'sheet' : 'center')
              )}
              style={{
                maxHeight: isSheet ? wagamourV2ModalMaxHeight.sheet : wagamourV2ModalMaxHeight.center,
                isolation: 'isolate',
                background: wagamourV2Surface.modal.background,
                boxShadow: wagamourV2ModalShellBodyGlow,
                WebkitBackdropFilter: `blur(${wagamourV2SurfaceBlur})`,
                backdropFilter: `blur(${wagamourV2SurfaceBlur})`,
                ...v2ModalShellDefaultTextStyle,
                ...v2ModalShellInnerRadiusStyle(isSheet ? 'sheet' : 'center'),
              }}
            >
          <WagamourV2Surface
            variant="modal"
            stacked
            fillShell
            className="flex h-full min-h-0 flex-col"
            hideBloom={false}
            hideEdgeHighlight
          >
            {isSheet && (
              <div className="flex justify-center pt-2 pb-1 flex-shrink-0" aria-hidden>
                <div className="h-1 w-12 rounded-full bg-white/30" />
              </div>
            )}

            {(header || showCloseButton) && (
              <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-white/10 px-4 pb-3 pt-3">
                <div className="min-w-0 flex-1">{header}</div>
                {showCloseButton && (
                  <button
                    type="button"
                    onClick={() => {
                      if (!preventClose) onClose()
                    }}
                    disabled={preventClose}
                    className={cn(
                      'flex-shrink-0 rounded-full p-2 text-white/70 transition-colors',
                      'hover:bg-white/10 hover:text-white',
                      'disabled:opacity-40',
                      playV2FocusRing
                    )}
                    aria-label="Close"
                  >
                    <X className="h-5 w-5" />
                  </button>
                )}
              </div>
            )}

            <div
              className={cn(
                'min-h-0 flex-1 overflow-y-auto overscroll-y-contain px-4 py-4',
                contentClassName
              )}
            >
              {children}
            </div>

            {footer && (
              <div className="flex-shrink-0 border-t border-white/10 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
                {footer}
              </div>
            )}
          </WagamourV2Surface>
            </div>
          </div>
        </div>
      </div>
    </div>,
    portalEl
  )
}
